import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://enact-dataspace.iti.gr/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kc_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const username = localStorage.getItem('username');
    if (username) {
      config.headers['X-Username'] = username;
    }

    // Add X-Api-Key header if apiKey is in the request data
    if (config.data?.apiKey) {
      config.headers['X-Api-Key'] = config.data.apiKey;
    }
    
    console.log(`🔵 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      data: config.data,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', error);
    console.error('❌ Error Response Data:', error.response?.data);
    console.error('❌ Error Status:', error.response?.status);
    
    const originalRequest = error.config;
    
    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('kc_refresh_token');
      if (refreshToken) {
        try {
          // Attempt token refresh
          // Fallbacks match config/keycloak.js - the Docker build context excludes
          // .env (see .dockerignore), so import.meta.env.VITE_* is otherwise undefined
          // in every deployed build, which silently broke this refresh call.
          const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'https://enact-auth.iti.gr';
          const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM || 'DAPS';
          const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'connector-1';
          const response = await axios.post(
            `${keycloakUrl}/realms/${keycloakRealm}/protocol/openid-connect/token`,
            new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: keycloakClientId,
              refresh_token: refreshToken,
            }),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
          );

          const { access_token, refresh_token } = response.data;
          localStorage.setItem('kc_token', access_token);
          localStorage.setItem('kc_refresh_token', refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // This used to be localStorage.clear() - wiping EVERYTHING,
          // including saved connector profiles, just because the login
          // session expired. Those are two unrelated things: a dead
          // session means "log in again", not "forget every connector
          // this account had configured". Only the actual auth keys get
          // removed; connectorProfiles_*/activeConnectorId_* etc. are left
          // alone (and get re-synced from the backend on the next login
          // anyway, so nothing is actually lost either way now).
          ['kc_token', 'kc_refresh_token', 'username', 'userRoles'].forEach(k => localStorage.removeItem(k));
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
